import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import InternalOrderService from '../../components/InternalOrderService';
import ProductService from '../../components/ProductService';
import StoresHelper from '../../components/storesHelper';
import Select from 'react-select';
import { toast } from 'react-toastify';
import '../../css/internalorders/board.css';

const STATUSES = [
  { value: 'pending', label: 'En attente' },
  { value: 'accepted', label: 'Acceptée' },
  { value: 'picked_up', label: 'Récupérée' },
  { value: 'delivered', label: 'Livrée' },
];

const CENTRAL_RECEIVER_STORE_ID = '6794efdb748a6934506e363d';

const emptyOrder = {
  requester_store_id: '',
  receiver_store_id: CENTRAL_RECEIVER_STORE_ID,
  products: [{ product_id: '', product_name: '', quantity: 1 }],
};

export default function InternalOrdersBoard() {
  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const defaultStoreId = sessionStorage.getItem('default_store_id');

  const isAdmin     = roles.includes('admin');
  const isManager   = roles.includes('manager');
  const isOperator  = roles.includes('operator') || roles.includes('driver');   // <-- opérateur/driver
  const isCarrefour = roles.includes('carrefour');

  const isRestrictedCarrefour = isCarrefour && !isAdmin && !isManager;

  const [orders, setOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [filters, setFilters] = useState({ status: '', store: '', date: '' });
  const [formData, setFormData] = useState(emptyOrder);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInit();
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const fetchInit = async () => {
    const storesList = await StoresHelper();
    setStores(storesList);

    // 👉 OPÉRATEUR : pas de pré-filtre magasin
    // 👉 CARREFOUR : pré-filtre sur son magasin par défaut
    if (isRestrictedCarrefour && defaultStoreId) {
      setFilters(f => ({ ...f, store: defaultStoreId }));
      setFormData(f => ({ ...f, requester_store_id: defaultStoreId, receiver_store_id: CENTRAL_RECEIVER_STORE_ID }));
    } else {
      setFormData(f => ({ ...f, receiver_store_id: f.receiver_store_id || CENTRAL_RECEIVER_STORE_ID }));
    }

    try {
      const prods = await ProductService.fetchProducts();
      const map = {};
      (prods || []).forEach(p => { map[p._id || p.id] = p.name; });
      setProductMap(map);
    } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let data = await InternalOrderService.fetchOrders({ status: filters.status });

      // 👉 OPÉRATEUR : aucun filtrage par store -> voit tout
      if (!isOperator) {
        if (isRestrictedCarrefour && defaultStoreId) {
          // Carrefour : limiter aux demandes impliquant son store
          data = (data || []).filter(
            o =>
              String(o.requester_store_id) === String(defaultStoreId) ||
              String(o.receiver_store_id) === String(defaultStoreId)
          );
        } else if (filters.store) {
          // Admin/Manager : filtre manuel par store si choisi
          data = (data || []).filter(o => String(o.requester_store_id) === String(filters.store));
        }
      }

      setOrders(data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement');
      setOrders([]);
    }
    setLoading(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await InternalOrderService.updateStatus(id, status);
      fetchOrders();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      await InternalOrderService.removeOrder(id);
      toast.success('Commande supprimée');
      fetchOrders();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreate = async () => {
    for (const prod of formData.products) {
      if (!prod.product_id && !prod.product_name) {
        toast.error('Chaque produit doit avoir un ID ou un nom');
        return;
      }
    }
    try {
      await InternalOrderService.createOrder(formData);
      setFormData({ ...emptyOrder, requester_store_id: isRestrictedCarrefour ? defaultStoreId || '' : '' });
      toast.success('Commande créée');
      fetchOrders();
    } catch {
      toast.error('Erreur lors de la création');
    }
  };

  const addProductLine = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { product_id: '', product_name: '', quantity: 1 }],
    });
  };

  const handleProductChange = (index, field, value) => {
    const updated = formData.products.map((p, i) => (i === index ? { ...p, [field]: value } : p));
    setFormData({ ...formData, products: updated });
  };

  const receiverOptions = stores.filter(
    s => String(s.id) === CENTRAL_RECEIVER_STORE_ID || !['external', 'glovo', 'site', 'partenaire'].includes(s.type)
  );

  const requesterOptions = isRestrictedCarrefour && defaultStoreId
    ? stores.filter(s => String(s.id) === String(defaultStoreId))
    : stores;

  return (
    <div className="container mt-4">
      <h2>Commandes internes</h2>

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        {/* Filtre statut : visible pour tous */}
        <select
          className="form-select w-auto"
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">Tous les statuts</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* 👉 OPÉRATEUR : on CACHE le filtre des magasins */}
        {!isOperator && (
          <select
            className="form-select w-auto"
            value={filters.store}
            onChange={e => setFilters({ ...filters, store: e.target.value })}
            disabled={isRestrictedCarrefour} // carrefour : visible mais bloqué sur son store
          >
            <option value="">Tous les magasins</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}

        <input
          type="date"
          className="form-control w-auto"
          value={filters.date}
          onChange={e => setFilters({ ...filters, date: e.target.value })}
        />

        <button className="btn btn-outline-secondary" onClick={fetchOrders}>
          <i className="fa-solid fa-rotate"></i> Reload
        </button>

        <button className="btn btn-success" data-bs-toggle="modal" data-bs-target="#orderModal">
          Nouvelle commande
        </button>
      </div>

      {loading && <div>Chargement...</div>}

      <div className="internal-board">
        {STATUSES.map(s => (
          <div key={s.value} className="internal-column">
            <div className={`internal-column-header status-${s.value}`}>{s.label}</div>
            {orders.filter(o => o.status === s.value).map(o => (
              <div key={o._id} className="order-card">
                <div className="fw-bold">
                  {stores.find(st => String(st.id) === String(o.requester_store_id))?.label || o.requester_store_id}
                </div>
                <ul className="mb-1">
                  {(o.products || []).map((p, i) => (
                    <li key={i}>{productMap[p.product_id] || p.product_name || p.product_id} × {p.quantity}</li>
                  ))}
                </ul>
                <small className="text-muted">{new Date(o.createdAt).toLocaleString()}</small>
                <select
                  className="form-select form-select-sm mt-2"
                  value={o.status}
                  onChange={e => handleStatusChange(o._id, e.target.value)}
                  disabled={isOperator && ['picked_up', 'delivered'].includes(o.status)}
                >
                  {STATUSES.map(st => (
                    <option key={st.value} value={st.value}>
                      {st.label}
                    </option>
                  ))}
                </select>
                {(isAdmin || isManager) && (
                  <button className="btn btn-danger btn-sm mt-1" onClick={() => handleDelete(o._id)}>
                    Supprimer
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <Modal modalId="orderModal" title="Nouvelle commande" submitLabel="Créer" onSubmit={handleCreate} modalType="modal-lg">
        <div className="mb-3">
          <label className="form-label">Magasin demandeur</label>
          <select
            className="form-select"
            value={formData.requester_store_id}
            onChange={e => setFormData({ ...formData, requester_store_id: e.target.value })}
            disabled={isRestrictedCarrefour} // opérateur non bloqué ici (sauf si tu veux aussi le verrouiller)
          >
            <option value="">Choisir</option>
            {requesterOptions.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Magasin receveur</label>
          <select
            className="form-select"
            value={formData.receiver_store_id}
            onChange={e => setFormData({ ...formData, receiver_store_id: e.target.value })}
          >
            <option value="">Choisir</option>
            {receiverOptions.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-2">
          <button type="button" className="btn btn-sm btn-secondary" onClick={addProductLine}>
            Ajouter un produit
          </button>
        </div>

        {formData.products.map((p, i) => (
          <div key={i} className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-center">
                <div className="col-md-5">
                  <label className="form-label">Produit</label>
                  <Select
                    classNamePrefix="select"
                    styles={{
                      container: base => ({ ...base, width: '100%' }),
                      control: base => ({ ...base, minHeight: '38px' }),
                    }}
                    placeholder="Choisir un produit"
                    options={Object.entries(productMap).map(([id, name]) => ({ value: id, label: name }))}
                    value={p.product_id ? { value: p.product_id, label: productMap[p.product_id] } : null}
                    onChange={opt => handleProductChange(i, 'product_id', opt ? opt.value : '')}
                    isDisabled={p.product_name}
                  />
                </div>

                <div className="col-md-5">
                  <label className="form-label">Nom du produit</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nom du produit"
                    value={p.product_name}
                    onChange={e => handleProductChange(i, 'product_name', e.target.value)}
                    disabled={p.product_id}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Quantité</label>
                  <input
                    type="number"
                    className="form-control"
                    value={p.quantity}
                    onChange={e => handleProductChange(i, 'quantity', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </Modal>
    </div>
  );
}
